import React from 'react';
import numeral from 'numeral';
import { Link } from 'react-router-dom';
import { get, take } from 'lodash';
import { formatToPercent, formatToNumber } from '../../utils/shared';

export const movieAnticipationFollowersTotal = data =>
  data.map(obj => ({
    x: obj.date,
    y: obj.totalFollowers,
    yLabel: 'Followers',
    yLabelPercent: 'Growth Rate',
    percentChange: get(obj, 'newFollowers', 0) / get(obj, 'totalFollowers', 1)
  }));

export const movieAnticipationFollowersNew = data =>
  data.map(obj => ({
    x: obj.date,
    y: obj.newFollowers,
    yLabel: 'New Followers',
    percentChange: get(obj, 'newFollowers', 0) / get(obj, 'totalFollowers', 1)
  }));

export const anticipationAffinityDefaultSorts = ['affinityIndex'];

export const anticipationAffinityColumns = [
  {
    Header: 'Movie',
    accessor: 'movie',
    className: 'tvt-tabular-data-table__show-report-anticipation-show-name',
    Cell: ({ row: { original }, cell: { value } }) => (
      <Link to={`/movie/${original.movieId}`}>{value}</Link>
    )
  },
  {
    Header: 'Studio',
    accessor: 'studio',
    className: 'tvt-tabular-data-table__show-report-anticipation-network'
  },
  {
    Header: 'Overlapping Viewers',
    accessor: 'overlappingViewers',
    Cell: ({ cell: { value } }) => numeral(value).format('0,0'),
    sortDescFirst: true,
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__show-report-anticipation-overlapping-viewers caret-center'
  },
  {
    Header: 'Affinity Index',
    accessor: 'affinityIndex',
    Cell: ({ cell: { value } }) => numeral(value).format('0,0'),
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__show-report-anticipation-affinity-index  caret-center',
    sortDescFirst: true
  }
];

export const audienceWAVWColumnsDefaultSorts = [];
export const audienceWAVWColumns = [
  {
    Header: 'Platform',
    accessor: 'platformName',
    className: 'tvt-tabular-data-table__movie-report-anticipation-wavw-platform'
  },
  {
    Header: 'Views',
    accessor: 'voteCount',
    sortDescFirst: true,
    Cell: ({ cell: { value } }) => formatToNumber(value),
    sortType: 'basic',
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__movie-report-anticipation-wavw-views'
  },
  {
    Header: '% Views',
    accessor: 'voteShare',
    Cell: ({ cell: { value } }) => formatToPercent(value),
    sortType: 'basic',
    className:
      'tvt-tabular-data-table__td-kpi tvt-tabular-data-table__movie-report-anticipation-wavw-views-percent',
    sortDescFirst: true
  }
]

export const formatGenderDemo = ({ data, loading }) => {
  if (loading) {
    return [];
  }

  return data.map(g => {
    const { gender, index, viewers } = g;
    const viewerIndex = numeral(index * 100).format('0.0');
    return {
      id: gender,
      viewers,
      value: viewerIndex,
      label: (
        <tspan>
          <tspan className="user-demo-tspan__gender-gender">{gender}</tspan>
          <tspan className="user-demo-tspan__gender-viewerIndex" x={80}>
            {`${viewerIndex}%`}
          </tspan>
        </tspan>
      )
    };
  });
};

//

export const formatActorsFromQuery = ({ data = [] } = {}) => {
  const formattedCast = data.map(actor => ({
    id: actor.actorId,
    score: `${Math.round(actor.votingPercent * 100)}%`,
    total: `${numeral(actor.votes).format('0,0')} votes`,
    name: actor.actorName,
    character: actor.characterName,
    image: actor.actorImageUrl
  }));

  return take(formattedCast, 10);
};

const colors = [
  '#491d8b',
  '#6929c4',
  '#8a3ffc',
  '#a56eff',
  '#be95ff',
  '#d4bbff',
  '#e8daff',
  '#f6f2ff',
  '#d9fbfb',
  '#9ef0f0',
  '#3ddbd9',
  '#08bdba',
  '#009d9a',
  '#007d79',
  '#005d5d',
  '#004144'
];

const getEmotionColor = (val, MAX_VALUE = 1) => {
  const indexer = Math.floor(colors.length / 2);
  const segmentSize = MAX_VALUE / indexer;

  const colorIndex = Math.floor(val / segmentSize + indexer);
  return colors[colorIndex < colors.length ? colorIndex : colors.length - 1];
};

export const emotionMapping = {
  shocked: String.fromCodePoint(0x1f635),
  sad: String.fromCodePoint(0x1f62d),
  amused: String.fromCodePoint(0x1f606),
  thrilled: String.fromCodePoint(0x1f929),
  confused: String.fromCodePoint(0x1f642),
  bored: String.fromCodePoint(0x1f611),
  scared: String.fromCodePoint(0x1f631),
  frustrated: String.fromCodePoint(0x1f624),
  touched: String.fromCodePoint(0x1f97a),
  reflective: '🤔',
  tense: '😬',
  understood: '😌'
};

export const formatEmotionData = query => {
  if (query.loading) {
    return query;
  }

  return {
    data: query.data.map(d => ({
      ...d,
      color: getEmotionColor(d.emotionPercent)
    })),
    loading: query.loading
  };
};

export const emotionsAxisElement = ({ data }) => {
  if (!data || !data.length) {
    return null;
  }

  const elements = data
    .sort((a, b) => b.emotionPercent - a.emotionPercent)
    .map(e => (
      <div className="tvt-show-engagement__emotion-axis-element">
        <div>{emotionMapping[e.emotionName]}</div>
        <div>{e.emotionName}</div>
      </div>
    ));

  return (
    <div className="tvt-show-engagement__emotion-axis-wrapper">{elements}</div>
  );
};
